import { useState, useEffect, useMemo } from 'react';

import { InboundPartnr, Material, SurfaceTreatment, MaterialColor, MaterialDimension, AlternateMaterial } from '../types/CommonTypes';
import { mergeFirstNonEmpty } from '../utils/merge';

export type ConditionType = 'color' | 'dimension';


export interface PartNrAssignment {
  /** Called by connector hooks whenever the upstream part list changes. */
  setRawPartNrs: (parts: InboundPartnr[]) => void;

  /** Called whenever TotalMateria fires a material event (including density updates). */
  applyTMMaterial: (material: Material) => void;

  /** Called when the user selects a row in the surface-treatment grid. */
  selectTreatment: (treatment: SurfaceTreatment | undefined) => void;

  /** Commits pendingMaterial to the view. */
  assignMaterial: () => void;

  /** Commits pendingTreatment to the view. */
  assignSurfaceTreatment: () => void;

  /** Commits a color or dimension condition immediately. */
  assignCondition: (condition: MaterialColor | MaterialDimension, type: ConditionType) => void;

  /**
   * Applies a server-authoritative surface treatment as the initial assignment
   * (i.e. when the opening party sends a stId that we can resolve to full
   * server-side data). Sets both pendingTreatment and assignedTreatment without
   * marking hasTMSurface, so a later material change still clears the treatment.
   */
  applyInitialTreatment: (treatment: SurfaceTreatment) => void;

  /**
   * Adds the current pendingMaterial as an alternate base material for every
   * primary part (one new row per partNr in viewPartNrs).
   */
  assignAlternateMaterial: () => void;

  /** Sets the currently selected alternate row (for condition assignment). Null clears selection. */
  selectAlternate: (id: string | null) => void;

  /** Assigns a condition to a specific alternate row. */
  assignConditionToAlternate: (id: string, condition: MaterialColor | MaterialDimension, type: ConditionType) => void;

  /** Removes an alternate row by id. */
  removeAlternate: (id: string) => void;

  /** Removes all alternate rows. */
  clearAllAlternates: () => void;

  /** Pre-loads alternates received from the host application on connection. */
  initAlternates: (alternates: AlternateMaterial[]) => void;

  /** Back-fills abbId on all alternate rows matching materialId where abbId is not yet known. */
  patchAlternateAbb: (materialId: string, adb: string | null | undefined) => void;

  /**
   * Raw parts with the committed material and treatment overlaid.
   * Material and treatment only appear here after their respective assign actions.
   */
  viewPartNrs: InboundPartnr[];

  /** Current list of assigned alternate base materials. */
  alternateMaterials: AlternateMaterial[];

  /** ID of the currently selected alternate row (for condition assignment). */
  selectedAlternateId: string | null;

  /** Latest material from TM (or inferred on load). Feeds useSurfaceTreatmentService. */
  pendingMaterial: Material | undefined;

  /** Latest treatment selection (or inferred on load). Not yet committed. */
  pendingTreatment: SurfaceTreatment | undefined;

  /** Last committed material (reflected in viewPartNrs). */
  assignedMaterial: Material | undefined;

  /** Last committed treatment (reflected in viewPartNrs). */
  assignedTreatment: SurfaceTreatment | undefined;

  /** Last committed condition (color or dimension). */
  assignedCondition: MaterialColor | MaterialDimension | null;

  /** Type of the committed condition: 'color', 'dimension', or null. */
  assignedConditionType: ConditionType | null;

  /** True once TM has fired at least one real material event. */
  hasTMMaterial: boolean;

  /** True once the user has explicitly selected a surface treatment. */
  hasTMSurface: boolean;

  hasUnsavedChanges: boolean;
  clearUnsavedChanges: () => void;
  markUnsavedChanges: () => void;
}

export function usePartNrAssignment(): PartNrAssignment {
  const [rawPartNrs, setRawPartNrs] = useState<InboundPartnr[]>([]);
  const [pendingMaterial, setPendingMaterial] = useState<Material | undefined>(undefined);
  const [pendingTreatment, setPendingTreatment] = useState<SurfaceTreatment | undefined>(undefined);
  const [assignedMaterial, setAssignedMaterial] = useState<Material | undefined>(undefined);
  const [assignedTreatment, setAssignedTreatment] = useState<SurfaceTreatment | undefined>(undefined);
  const [assignedCondition, setAssignedCondition] = useState<MaterialColor | MaterialDimension | null>(null);
  const [assignedConditionType, setAssignedConditionType] = useState<ConditionType | null>(null);
  const [hasTMMaterial, setHasTMMaterial] = useState(false);
  const [hasTMSurface, setHasTMSurface] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [alternateMaterials, setAlternateMaterials] = useState<AlternateMaterial[]>([]);
  const [selectedAlternateId, setSelectedAlternateId] = useState<string | null>(null);

  useEffect(() => {
    if (rawPartNrs.length === 0) return;
    if(pendingMaterial || assignedMaterial) return;
    const merged = mergeFirstNonEmpty(rawPartNrs);
    if (!merged?.bmId) return;
    const mat: Material = {
      id: String(merged.bmId),
      name: merged.bmName ?? '',
      density: merged.bmDensity ?? '',
    };
    setAssignedMaterial(mat);
    setPendingMaterial(mat);
  }, [rawPartNrs]);

  useEffect(() => {
    if (rawPartNrs.length === 0) return;
    if(pendingTreatment || assignedTreatment) return;
    const merged = mergeFirstNonEmpty(rawPartNrs);
    setAssignedTreatment(prev => {
      if (!merged?.stMaterialId) return prev;
      return {
        stId: merged.stMaterialId,
        stName: merged.stName ?? '',
        thickness: merged.thickness ?? '',
        density: merged.stDensity ?? '',
      };
    });
  }, [rawPartNrs]);

  const applyTMMaterial = (material: Material) => {
    if(material.id){
      setHasTMMaterial(true);
      setPendingMaterial(prev => {
        if (prev?.id !== material.id) return material;
        return {
          ...material,
          ...(material.adb !== undefined ? {} : { adb: prev.adb }),
        };
      });
      setAssignedMaterial(prev => {
        if (prev?.id !== material.id) return prev;
        return {
          ...prev,
          ...(material.density  ? { density: material.density }  : {}),
          ...(material.name     ? { name: material.name }         : {}),
          ...(material.adb !== undefined ? { adb: material.adb } : {}),
        };
      });
      // Back-fill abbId on any alternate rows that were assigned before the fetch
      // completed (i.e. abbId was not yet known at assign time).
      // Store null (not undefined) so the batch-fetch does not re-trigger.
      if (material.adb !== undefined) {
        setAlternateMaterials(prev =>
          prev.map(a =>
            a.materialId === material.id && a.abbId === undefined
              ? { ...a, abbId: material.adb ?? null }
              : a
          )
        );
      }
    } else {
      setPendingMaterial(undefined);
      setHasTMMaterial(false);
    }
  };

  const selectTreatment = (treatment: SurfaceTreatment | undefined) => {
    setPendingTreatment(treatment);
    setHasTMSurface(true);
  };

  const assignMaterial = () => {
    const prevAssignedId = assignedMaterial?.id;
    setAssignedMaterial(pendingMaterial);
    // When the material changes, any previously committed treatment, condition and coverage are now stale.
    if (pendingMaterial?.id !== prevAssignedId) {
      let empty = {stId: '', stName: '', thickness: '', density: ''};
      setAssignedTreatment(empty);
      setPendingTreatment(empty);
      setHasTMSurface(false);
      setAssignedCondition(null);
      setAssignedConditionType(null);
      setRawPartNrs(prev => prev.map(p => ({ ...p, coverage: undefined })));
    }
    setHasUnsavedChanges(true);
  };

  const assignSurfaceTreatment = () => {
    setAssignedTreatment(pendingTreatment);
    setHasUnsavedChanges(true);
  };

  const assignCondition = (condition: MaterialColor | MaterialDimension, type: ConditionType) => {
    setAssignedCondition(condition);
    setAssignedConditionType(type);
    setHasUnsavedChanges(true);
  };

  const applyInitialTreatment = (treatment: SurfaceTreatment) => {
    setPendingTreatment(treatment);
    setAssignedTreatment(treatment);
  };

  const assignAlternateMaterial = () => {
    if (!pendingMaterial?.id) return;
    const mat = pendingMaterial;

    const plainName = mat.name ?? mat.id;

    // If an alternate row is selected, update that row's material and clear its condition.
    if (selectedAlternateId) {
      setAlternateMaterials(prev =>
        prev.map(a =>
          a.id === selectedAlternateId
            ? { ...a, materialId: mat.id, materialName: plainName, abbId: mat.adb ?? undefined, materialDensity: mat.density, condition: null, conditionType: null }
            : a
        )
      );
      setHasUnsavedChanges(true);
      return;
    }

    if (rawPartNrs.length === 0) return;

    setAlternateMaterials(prev => {
      const newRows: AlternateMaterial[] = rawPartNrs.map(p => ({
        id: crypto.randomUUID(),
        partNr: p.partnr,
        materialId: mat.id,
        materialName: plainName,
        abbId: mat.adb ?? undefined,
        materialDensity: mat.density,
        conditionType: null,
        condition: null,
      }));
      return [...prev, ...newRows];
    });
    setHasUnsavedChanges(true);
  };

  const selectAlternate = (id: string | null) => {
    setSelectedAlternateId(id);
  };

  const assignConditionToAlternate = (id: string, condition: MaterialColor | MaterialDimension, type: ConditionType) => {
    setAlternateMaterials(prev =>
      prev.map(a => a.id === id ? { ...a, condition, conditionType: type } : a)
    );
    setHasUnsavedChanges(true);
  };

  const removeAlternate = (id: string) => {
    setAlternateMaterials(prev => prev.filter(a => a.id !== id));
    setSelectedAlternateId(prev => prev === id ? null : prev);
    setHasUnsavedChanges(true);
  };

  const clearAllAlternates = () => {
    setAlternateMaterials([]);
    setSelectedAlternateId(null);
    setHasUnsavedChanges(true);
  };

  const initAlternates = (alternates: AlternateMaterial[]) => {
    setAlternateMaterials(alternates);
  };

  const patchAlternateAbb = (materialId: string, adb: string | null | undefined) => {
    setAlternateMaterials(prev =>
      prev.map(a =>
        a.materialId === materialId && a.abbId === undefined
          ? { ...a, abbId: adb ?? null }
          : a
      )
    );
  };

  const viewPartNrs = useMemo<InboundPartnr[]>(() => {
    return rawPartNrs.map(p => ({
      ...p,
      ...(assignedMaterial
        ? {
            bmName: assignedMaterial.name,
            bmId: assignedMaterial.id,
            bmDensity: assignedMaterial.density ?? '',
          }
        : {}),
      ...(assignedTreatment
        ? {
            stName: assignedTreatment.stName,
            stMaterialId: assignedTreatment.stId || undefined,
            thickness: assignedTreatment.thickness,
            stDensity: assignedTreatment.density,
          }
        : {}),
    }));
  }, [rawPartNrs, assignedMaterial, assignedTreatment]);

  return {
    setRawPartNrs: (parts: InboundPartnr[]) => {
      setRawPartNrs(parts);
    },
    applyTMMaterial,
    selectTreatment,
    assignMaterial,
    assignSurfaceTreatment,
    assignCondition,
    applyInitialTreatment,
    assignAlternateMaterial,
    selectAlternate,
    assignConditionToAlternate,
    removeAlternate,
    clearAllAlternates,
    initAlternates,
    patchAlternateAbb,
    viewPartNrs,
    alternateMaterials,
    selectedAlternateId,
    pendingMaterial,
    pendingTreatment,
    assignedMaterial,
    assignedTreatment,
    assignedCondition,
    assignedConditionType,
    hasTMMaterial,
    hasTMSurface,
    hasUnsavedChanges,
    clearUnsavedChanges: () => setHasUnsavedChanges(false),
    markUnsavedChanges: () => setHasUnsavedChanges(true),
  };
}
